import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import chevronGrayIcon from 'assets/chevron-gray.svg';
import searchIcon from 'assets/search_icon.svg';

import styles from './dropdownStyles.module.scss';

const FlowDropdown = ({ value, onChange, items, placeholder, triggerSize, disabled, heading, showSearch }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const filteredItems = showSearch
    ? items.filter(item => item.toLowerCase().includes(searchTerm.toLowerCase()))
    : items;

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      if (showSearch) {
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, showSearch]);

  const handleSelect = item => {
    onChange(item);
    setIsOpen(false);
    setSearchTerm('');
  };

  const triggerClass = triggerSize === 'large' ? styles.categoryTrigger : styles.subFlowTrigger;
  const triggerTextClass = triggerSize === 'large' ? styles.categoryTriggerText : styles.subFlowTriggerText;
  const dropdownMenuClass = heading ? styles.dropdownMenuSubFlow : styles.dropdownMenu;
  const dropdownListClass = heading ? styles.dropdownListSubFlow : styles.dropdownList;

  return (
    <div className={styles.dropdownContainer} ref={containerRef}>
      <button
        type="button"
        className={triggerClass}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className={triggerTextClass}>
          {value || placeholder}
        </span>
        <img
          src={chevronGrayIcon}
          alt=""
          className={`${styles.chevronIcon} ${isOpen ? styles.chevronFlipped : ''}`}
        />
      </button>

      {isOpen && (
        <div className={dropdownMenuClass}>
          {showSearch && (
            <div className={styles.searchWrapper}>
              <div className={styles.searchInput}>
                <img src={searchIcon} alt="" width={20} height={20} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className={styles.searchField}
                />
              </div>
            </div>
          )}

          <div className={dropdownListClass}>
            {heading && (
              <div className={styles.dropdownHeading}>
                {heading}
              </div>
            )}
            {filteredItems.map(item => (
              <button
                key={item}
                type="button"
                className={`${styles.dropdownItem} ${value === item ? styles.dropdownItemSelected : ''}`}
                onClick={() => handleSelect(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

FlowDropdown.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  placeholder: PropTypes.string,
  triggerSize: PropTypes.oneOf(['large', 'small']),
  disabled: PropTypes.bool,
  heading: PropTypes.string,
  showSearch: PropTypes.bool,
};

FlowDropdown.defaultProps = {
  value: '',
  placeholder: 'Select',
  triggerSize: 'large',
  disabled: false,
  heading: null,
  showSearch: true,
};

export default FlowDropdown;
